import { useApolloClient, useMutation, useLazyQuery } from '@apollo/client';
import ADD_LEGAL_DOCUMENT from '../queries/addLegalDocumentMutation';
import DELETE_LEGAL_DOCUMENT from '../queries/deleteLegalDocumentMutation';
import GET_LEGAL_DOCUMENT_FILE_URL from '../queries/getLegalDocumentFileURLQuery';
import LegalDocumentsMapper from '../mappers/LegalDocumentsMapper';
import { showErrorToast, showSuccessToast } from '@shared/services/toastService';
import { removeEntityFromCache } from '../../shared/services/updateCacheField';
import MESSAGE_TARGETS from '../../../signalR/constants/signalRMessageTargets';
import { useNonBlockingFileUpload } from '@shared/hooks/useNonBlockingFileUpload';

const LEGAL_DOCUMENTS_CACHE_FIELD = 'legalDocuments';
const SAVE_LEGAL_DOCUMENT_SUCCESS_MESSAGE = 'Legal document has been added successfully.';
const DELETE_LEGAL_DOCUMENT_SUCCESS_MESSAGE = 'Legal document has been removed successfully.';
const UPLOAD_FILE_ERROR_MESSAGE = 'Unable to upload file.';
const DOWNLOAD_FILE_ERROR_MESSAGE = 'An error occurred while fetching the download link.';
const SAVING_LEGAL_DOCUMENT_MESSAGE = 'Saving legal document...';

export const useManageLegalDocuments = (orgId, refetchLegalDocuments) => {
  const apolloClient = useApolloClient();

  const [addLegalDocumentMutation, { error: addLegalDocumentError }] =
    useMutation(ADD_LEGAL_DOCUMENT);
  const [deleteLegalDocumentMutation, { error: deleteLegalDocumentError }] =
    useMutation(DELETE_LEGAL_DOCUMENT);
  const [getDocumentFileUrlQuery, { error: getFileUrlError }] = useLazyQuery(
    GET_LEGAL_DOCUMENT_FILE_URL,
    {
      fetchPolicy: 'network-only'
    }
  );

  const { uploadFile } = useNonBlockingFileUpload({
    loadingMessage: SAVING_LEGAL_DOCUMENT_MESSAGE,
    successMessage: SAVE_LEGAL_DOCUMENT_SUCCESS_MESSAGE,
    errorMessage: UPLOAD_FILE_ERROR_MESSAGE,
    signalRMessageTarget: MESSAGE_TARGETS.LEGAL_FILE_VALIDATION_NOTIFICATION,
    onSuccess: refetchLegalDocuments
  });

  const handleAddLegalDocument = async legalDocument => {
    const { file } = legalDocument;

    const fileUrlProvider = async () => {
      const { data, errors } = await addLegalDocumentMutation({
        variables: {
          legalDocument: LegalDocumentsMapper.toApolloInput(legalDocument),
          orgId
        }
      });

      if (!data?.addLegalDocument || errors) {
        throw new Error('Failed to add legal document record.');
      }

      return data.addLegalDocument;
    };

    await uploadFile(file, fileUrlProvider);
  };

  const handleDeleteLegalDocument = async id => {
    const { data } = await deleteLegalDocumentMutation({ variables: { legalDocumentId: id } });
    removeEntityFromCache(
      apolloClient.cache,
      LEGAL_DOCUMENTS_CACHE_FIELD,
      data.deleteLegalDocument
    );
    showSuccessToast(DELETE_LEGAL_DOCUMENT_SUCCESS_MESSAGE);
  };

  const handleDownloadLegalDocumentFile = async legalDocumentId => {
    const { data, error: downloadError } = await getDocumentFileUrlQuery({
      variables: { legalDocumentId }
    });

    if (downloadError) {
      showErrorToast(DOWNLOAD_FILE_ERROR_MESSAGE);
      return;
    }

    window.open(data.legalDocumentFileUrl, '_blank');
  };

  return {
    handleAddLegalDocument,
    handleDeleteLegalDocument,
    handleDownloadLegalDocumentFile,
    addLegalDocumentError,
    deleteLegalDocumentError,
    getFileUrlError
  };
};
